import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { Box, Button } from '@mui/material';
import ChartModal from './ChartModal';
import { useState } from 'react';

const _RAIN_COLOR = '#0086ff';
const _CHART_COLORS = [
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#2ca02c',
  '#ff7f0e',
  '#1f77b4',
  '#ff9896',
  '#98df8a',
  '#ffbb78',
  '#aec7e8',
  '#ff7f7f',
  '#c5b0d5',
  '#c49c94',
  '#f7b6d2',
  '#c7c7c7',
  '#dbdb8d',
  '#9edae5',
  '#e377c2',
  '#8c564b',
  '#17becf',
  '#bcbd22',
  '#7f7f7f',
  '#e377c2',
  '#8c564b',
  '#ff9896',
  '#98df8a',
  '#ffbb78',
  '#aec7e8',
  '#ff7f7f',
  '#c5b0d5',
  '#c49c94',
  '#f7b6d2',
  '#c7c7c7',
  '#dbdb8d',
  '#9edae5',
  '#e377c2',
  '#8c564b',
  '#17becf',
  '#bcbd22',
];

const Charts = ({
  series,
  options,
  titleText,
  type,
  enableDataLabel = false,
  isDraggable = false,
  colors = _CHART_COLORS,
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const hideYAxisLabel = false;
  const getAxisTitle = (axisType) => {
    return series.reduce((acc, curr) => {
      const axis = curr.axes.find((axis) => axis.axis_type === axisType);
      if (axis) {
        return axis.axis_label;
      }
      return acc;
    }, '');
  };

  const formatYAxisValue = (value) => {
    if (value == null) {
      return null;
    }
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  const isRainfallSeries = (seriesItem) => {
    return (
      seriesItem.axes.find((axis) => axis.axis_col === 'rainfall_inches') ||
      (seriesItem.name && seriesItem.name.toLowerCase().includes('rainfall'))
    );
  };

  const getCategories = () => {
    if (series.length === 0) {
      return { categories: [], axisType: '' };
    }
    const categories = series.flatMap((seriesItem) => {
      const xAxis = seriesItem.axes.find((axis) => axis.axis_type === 'x_axis');
      if (xAxis) {
        const xAxisValue = xAxis.axis_col;
        return seriesItem.data.map((dataItem) => dataItem[xAxisValue]);
      }
      return [];
    });
    categories.sort();
    const uniqueCategories = Array.from(new Set(categories));
    const axisType = series[0].axes.find((axis) => axis.axis_type === 'x_axis')['value_type'];
    return { categories: uniqueCategories, axisType };
  };

  const getDataPoints = () => {
    const { categories }: { categories: any } = getCategories();
    const dataPoints: { name?: string; data: any[]; color?: string }[] = [];
    series.forEach((seriesItem, index) => {
      const yAxis = seriesItem.axes.find((axis) => axis.axis_type === 'y_axis');
      const xAxis = seriesItem.axes.find((axis) => axis.axis_type === 'x_axis');

      const dataConvertedDict = seriesItem.data.reduce((result, current) => {
        result[current[xAxis.axis_col]] = current;
        return result;
      }, {});
      const rainfallSeries = isRainfallSeries(seriesItem);
      const seriesColor = rainfallSeries ? _RAIN_COLOR : colors[index % colors.length];

      if (seriesItem.name) {
        dataPoints.push({
          name: seriesItem.name,
          data: categories.map((cat) =>
            dataConvertedDict[cat] ? dataConvertedDict[cat][yAxis.axis_col] : null,
          ),
          color: seriesColor,
        });
      } else {
        dataPoints.push({
          data: categories.map((cat) =>
            dataConvertedDict[cat] ? dataConvertedDict[cat][yAxis.axis_col] : null,
          ),
          color: seriesColor,
        });
      }
    });
    return dataPoints;
  };

  const getChartOptions = (series) => {
    const yAxisValues = series.flatMap((s) => s.data);
    const yAxisMin = Math.min(...yAxisValues);
    const yAxisMax = Math.max(...yAxisValues);
    const yAxisMinMaxOffset = type === 'bar' ? 0 : 2;

    const { categories, axisType }: { categories: any; axisType: string } = getCategories();

    const seriesColors = series.map((s) => s.color);

    const chartOptions = {
      chart: {
        type,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        stacked: false,
      },
      redrawOnParentResize: true,
      colors: seriesColors,
      dataLabels: {
        enabled: enableDataLabel,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
      },
      stroke: {
        width: 2,
        curve: 'straight',
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      markers: {
        colors: seriesColors,
        strokeColors: seriesColors,
      },
      title: {
        text: titleText,
        align: 'left',
      },
      xaxis: {
        categories: categories,
        type: axisType,
        tickAmount: 5,
        tickPlacement: 'between',
        title: {
          text: getAxisTitle('x_axis'),
          rotate: 0,
        },
        labels: {
          rotate: 0,
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: formatYAxisValue,
          show: !hideYAxisLabel,
        },
        title: {
          text: hideYAxisLabel ? '' : getAxisTitle('y_axis'),
        },
        min: yAxisMin - yAxisMinMaxOffset,
        max: yAxisMax + yAxisMinMaxOffset,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: formatYAxisValue,
        },
      },
      ...options,
    };
    return chartOptions;
  };

  const getModelChartOptions = (chartOptions) => {
    const modalChartOptions = {
      ...chartOptions,
      yaxis: {
        ...chartOptions.yaxis,
        labels: {
          ...chartOptions.yaxis.labels,
          show: true,
        },
        title: {
          ...chartOptions.yaxis.title,
          text: hideYAxisLabel ? '' : getAxisTitle('y_axis'),
        },
      },
      chartToolbar: {
        show: true,
      },
    };
    return modalChartOptions;
  };

  const dataPoints = getDataPoints();
  const chartOptions = getChartOptions(dataPoints);

  if (!type) return null;
  return (
    <>
      <Box sx={{ position: 'absolute', right: 16, top: 0, zIndex: 2 }}>
        <Button
          variant='text'
          size='small'
          aria-label='View Detail Button'
          onClick={handleOpen}
          disabled={isDraggable}
        >
          View Detail
        </Button>
      </Box>
      <ChartModal
        open={open}
        handleClose={handleClose}
        modalChartOptions={getModelChartOptions(chartOptions)}
        seriesData={getDataPoints()}
        type={type}
        title={titleText}
        categories={getCategories()}
      />
      <ReactApexChart
        height='100%'
        width='100%'
        options={chartOptions}
        series={dataPoints}
        type={type}
      />
    </>
  );
};

Charts.propTypes = {
  series: PropTypes.array.isRequired,
  options: PropTypes.shape({}),
  type: PropTypes.oneOf(['line', 'area', 'bar']),
  colors: PropTypes.arrayOf(PropTypes.string),
  titleText: PropTypes.string.isRequired,
  enableDataLabel: PropTypes.bool,
  isDraggable: PropTypes.bool,
};

export default Charts;
